import { useState, useRef, useEffect } from "react";
import { Loader2, Smartphone, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";
import Modal from "../components/Modal";
import Field, { inputStyle, selectStyle, inputErrorStyle } from "../components/Field";
import QRCode from "../components/QRCode";
import PerfilCampos from "../components/PerfilCampos";
import { EscolherTabela } from "../components/TabelasComerciais";
import BuscaParceiro from "../components/BuscaParceiro";
import {
  SISTEMAS, TIPOS_ESTABELECIMENTO, TIPOS_VEICULO, SPECIALTY_GROUPS, MECH_SPECIALTIES, BRAZILIAN_STATES,
  SERVICOS_PRESTADOS, SERVICOS_TERRESTRES, ASSISTENCIA_TIPOS, DIAS_SEMANA, DIAS_UTEIS, servicoLabel, resumoAtendimento,
  perfilDe, resumoPerfil, tipoPorAtividade, docsDaFicha, docsComplementaresFaltando,
  secoesDaFicha, modeloDaFicha, faltandoNoPerfil, limparPerfil, tituloSecaoServicos, condicoesComerciaisTexto,
  copiaDaTabela, condicoesADefinir,
} from "../constants";
import { ufsCondicaoExclusiva } from "../lib/tabelasComerciais";
import { fetchANAC, anacParaPerfil, SITUACOES_IMPEDITIVAS } from "../lib/anac";
import { generateId, getToday, maskCNPJ, maskPhone, maskCEP, fetchCNPJ, fetchCEP } from "../utils";
import { uploadToStorage, getSignedUrl, removeFromStorage, prepararUpload } from "../lib/storage";
import { upsertRow } from "../lib/database";
import { sincronizarAceite } from "../lib/manutencaoSync";
import { emitirCartaoCNPJ } from "../lib/cnpjComprovante";
import { registrarAceite } from "../lib/aceite";
import { encodeFichaToken, fichaFormUrl, whatsappUrl } from "../lib/fichaLink";
import { fetchANP, anpParaPerfil } from "../lib/anp";
import { gerarTermoPDF, baixarBlob } from "../utils/gerarTermoPDF";
import TermosAceite from "../components/TermosAceite";
import DocsCredenciamento from "../components/DocsCredenciamento";

const DRAFT_KEY = "cred_draft_v1";

const empty = {
  partnerId: "", sistemas: ["manutencao"], tipoEstabelecimento: "oficina",
  razaoSocial: "", nomeFantasia: "", cnpj: "", inscricaoEstadual: "", atividade: "", simplesNacional: "",
  rua: "", numero: "", bairro: "", cidade: "", estado: "SP", cep: "", pais: "Brasil",
  telefone: "", celular: "", email: "",
  responsavelNome: "", responsavelDoc: "", responsavelRg: "", responsavelTelefone: "", responsavelCargo: "", responsavelEmail: "",
  representanteNome: "", representanteCargo: "",
  banco: "", agencia: "", contaCorrente: "", tipoConta: "", titularConta: "", chavePix: "",
  servicos: [], tiposVeiculo: [], especialidades: [], perfil: {},
  fazSocorro: null, atende24h: false, assistenciaRodovias: false,
  tiposAssistencia: [], diasAtendimento: [], horarioInicio: "", horarioFim: "",
  telefoneSocorro: "", rodoviasAtendidas: "",
  dataAssinatura: getToday(), aceiteTermos: false, notes: "",
};

// Chip toggle reutilizável
function Chips({ options, selected, onToggle, cols = 3 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 6 }}>
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onToggle(o.id)}
            style={{
              display: "flex", alignItems: "center", gap: 6, padding: "7px 10px",
              borderRadius: 8, cursor: "pointer", fontSize: 12, textAlign: "left",
              border: on ? "1.5px solid #7c3aed" : "1px solid #e2e8f0",
              background: on ? "#f5f3ff" : "#fff",
              color: on ? "#6d28d9" : "#475569", fontWeight: on ? 600 : 400,
            }}
          >
            <span style={{
              width: 15, height: 15, borderRadius: 4, flexShrink: 0,
              border: on ? "none" : "1.5px solid #cbd5e1",
              background: on ? "#7c3aed" : "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: 11, lineHeight: 1,
            }}>{on ? "✓" : ""}</span>
            {o.icon ? `${o.icon} ` : ""}{o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function NewCredenciamento({ open, onClose, setCredenciamentos, partners = [], userId, userName = "", editing = null }) {
  const [data, setData] = useState({ ...empty });
  const [errors, setErrors] = useState({});
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [anpInfo, setAnpInfo] = useState(null);
  const [anacInfo, setAnacInfo] = useState(null);
  const [cepLoading, setCepLoading] = useState(false);
  const [pendingDocs, setPendingDocs] = useState([]); // [{tipo,path,nome}] já enviados ao Storage
  const [existingDocs, setExistingDocs] = useState([]); // [{path,nome}] de fichas já salvas (edição)
  const [removedDocs, setRemovedDocs] = useState([]); // paths a remover do storage no save
  const [existingSignatureUrl, setExistingSignatureUrl] = useState(null); // dataURL da assinatura salva/recuperada
  const [processingDocs, setProcessingDocs] = useState(false);
  const [aceiteInfo, setAceiteInfo] = useState({ pronto: false, aceites: [] });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [showEsp, setShowEsp] = useState(false);
  const [verTodosServicos, setVerTodosServicos] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showDocsEmpresa, setShowDocsEmpresa] = useState(false);
  const idRef = useRef(null); // id estável da ficha (usado nos caminhos do Storage)
  const tipoTocado = useRef(false); // escolha manual do ramo vence a detecção pelo CNPJ
  const isEdit = !!editing;
  // Aceitou = leu e marcou o click-wrap agora, OU a ficha já vinha aceita (edição).
  const aceitou = aceiteInfo.pronto || !!(editing && editing.aceiteTermos);

  const set = (patch) => setData((p) => ({ ...p, ...patch }));
  const toggle = (key, id) =>
    setData((p) => ({ ...p, [key]: p[key].includes(id) ? p[key].filter((x) => x !== id) : [...p[key], id] }));

  // Ramo escolhido → perguntas extras da ficha (null = ficha padrão)
  const perfilAtual = perfilDe(data.tipoEstabelecimento);
  // Mesma regra do formulário público: a ficha pergunta o que os sistemas
  // marcados usam. Posto que vai só para o combustível não responde catálogo de
  // oficina nem socorro 24h — ver secoesDaFicha em constants.
  const secoes = secoesDaFicha(data.sistemas, data.tipoEstabelecimento);
  // O modelo só nomeia o link (`t`): Aeronáutico quando o ramo é de aeronave.
  const modelo = modeloDaFicha({ tipo: data.tipoEstabelecimento, sistemas: data.sistemas });
  const temSocorro = secoes.socorro;
  // Anexos da ficha: os já salvos mais os desta sessão. Os dois blocos de
  // documento (assinante e empresa) leem a MESMA lista.
  const todosDocs = [...existingDocs, ...pendingDocs];
  const docsFaltandoAqui = docsComplementaresFaltando(todosDocs, data);
  // Trocar o ramo zera o perfil: bandeira de posto não vale para borracharia.
  const trocarTipo = (tipo) => {
    const def = perfilDe(tipo);
    tipoTocado.current = true;
    set({ tipoEstabelecimento: tipo, perfil: {}, ...(def && def.veiculos === false ? { tiposVeiculo: [] } : {}) });
    setVerTodosServicos(false);
  };

  // Prefill ao abrir (nova, edição ou recuperação de rascunho)
  useEffect(() => {
    if (!open) return;
    idRef.current = editing?.id || generateId();
    // Em edição o ramo salvo manda; em ficha nova o CNPJ pode sugerir.
    tipoTocado.current = !!editing;
    setErrors({});
    setRemovedDocs([]);
    setExistingSignatureUrl(null);

    if (editing) {
      setData({
        partnerId: editing.partnerId || "",
        sistemas: editing.sistemas || ["manutencao"],
        tipoEstabelecimento: editing.tipoEstabelecimento || "oficina",
        razaoSocial: editing.razaoSocial || "", nomeFantasia: editing.nomeFantasia || "", cnpj: editing.cnpj || "",
        inscricaoEstadual: editing.inscricaoEstadual || "", atividade: editing.atividade || "", simplesNacional: editing.simplesNacional || "",
        rua: editing.rua || "", numero: editing.numero || "", bairro: editing.bairro || "",
        cidade: editing.cidade || "", estado: editing.estado || "SP", cep: editing.cep || "", pais: editing.pais || "Brasil",
        telefone: editing.telefone || "", celular: editing.celular || "", email: editing.email || "",
        responsavelNome: editing.responsavelNome || "", responsavelDoc: editing.responsavelDoc || "", responsavelRg: editing.responsavelRg || "", responsavelTelefone: editing.responsavelTelefone || "", responsavelCargo: editing.responsavelCargo || "", responsavelEmail: editing.responsavelEmail || "",
        representanteNome: editing.representanteNome || "", representanteCargo: editing.representanteCargo || "",
        banco: editing.banco || "", agencia: editing.agencia || "", contaCorrente: editing.contaCorrente || "", tipoConta: editing.tipoConta || "", titularConta: editing.titularConta || "", chavePix: editing.chavePix || "",
        servicos: editing.servicos || [], tiposVeiculo: editing.tiposVeiculo || [], especialidades: editing.especialidades || [],
        perfil: editing.perfil && typeof editing.perfil === "object" ? editing.perfil : {},
        fazSocorro: typeof editing.fazSocorro === "boolean" ? editing.fazSocorro : null,
        atende24h: !!editing.atende24h, assistenciaRodovias: !!editing.assistenciaRodovias,
        tiposAssistencia: editing.tiposAssistencia || [], diasAtendimento: editing.diasAtendimento || [],
        horarioInicio: editing.horarioInicio || "", horarioFim: editing.horarioFim || "",
        telefoneSocorro: editing.telefoneSocorro || "", rodoviasAtendidas: editing.rodoviasAtendidas || "",
        dataAssinatura: editing.dataAssinatura || getToday(), aceiteTermos: editing.aceiteTermos ?? !!editing.assinaturaPath, notes: editing.notes || "",
      });
      setExistingDocs(editing.documentos || []);
      setPendingDocs([]);
      if (editing.assinaturaPath) {
        (async () => {
          const u = await getSignedUrl(editing.assinaturaPath);
          if (!u) return;
          try {
            const resp = await fetch(u);
            const blob = await resp.blob();
            const reader = new FileReader();
            reader.onloadend = () => setExistingSignatureUrl(reader.result);
            reader.readAsDataURL(blob);
          } catch { /* ignora */ }
        })();
      }
    } else {
      // nova ficha: recupera rascunho (após crash/fechamento)
      let restored = null;
      try { const raw = localStorage.getItem(DRAFT_KEY); if (raw) restored = JSON.parse(raw); } catch { /* ignora */ }
      setData(restored && typeof restored === "object" ? { ...empty, ...restored } : { ...empty });
      setExistingDocs([]);
      try { const rawD = localStorage.getItem(DRAFT_KEY + "_docs"); setPendingDocs(rawD ? (JSON.parse(rawD) || []) : []); } catch { setPendingDocs([]); }
    }
  }, [open, editing]);

  // Autosalva rascunho (nova ficha) — texto, seleções, fotos já enviadas e assinatura
  useEffect(() => {
    if (!open || editing) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
        localStorage.setItem(DRAFT_KEY + "_docs", JSON.stringify(pendingDocs));
      } catch { /* quota/privado: ignora */ }
    }, 800);
    return () => clearTimeout(t);
  }, [data, pendingDocs, open, editing]);

  // Prefill ao escolher um parceiro já existente
  const handlePartner = (id) => {
    const p = partners.find((x) => x.id === id);
    if (!p) { set({ partnerId: "" }); return; }
    const typeMap = { oficina: "oficina", funilaria: "funilaria", borracharia: "borracharia", posto: "posto", autopeca: "autopecas", eletrica: "eletrica" };
    const novoTipo = typeMap[p.type] || data.tipoEstabelecimento;
    // O ramo veio do cadastro do parceiro: é escolha, não palpite do CNPJ.
    if (typeMap[p.type]) tipoTocado.current = true;
    set({
      partnerId: id,
      tipoEstabelecimento: novoTipo,
      // O parceiro pode mudar o ramo; o perfil do ramo anterior não se aproveita.
      ...(novoTipo !== data.tipoEstabelecimento ? { perfil: {} } : {}),
      razaoSocial: p.razaoSocial || p.name || "",
      nomeFantasia: p.nomeFantasia || p.name || "",
      cnpj: p.cnpj || "",
      telefone: p.phone || "",
      responsavelNome: p.contact || "",
      estado: p.region && p.region.length === 2 ? p.region : "SP",
    });
  };

  const handleCNPJ = async (val) => {
    const masked = maskCNPJ(val);
    set({ cnpj: masked });
    const digits = masked.replace(/\D/g, "");
    if (digits.length === 14) {
      setCnpjLoading(true);
      const r = await fetchCNPJ(digits);
      setCnpjLoading(false);
      if (r) {
        // A atividade do CNPJ já diz o ramo — só não atropela quem escolheu à mão.
        const detectado = tipoTocado.current ? null : tipoPorAtividade(r.atividadePrincipal);
        setData((p) => ({
          ...p,
          ...(detectado && detectado !== p.tipoEstabelecimento
            ? { tipoEstabelecimento: detectado, perfil: {}, ...(perfilDe(detectado)?.veiculos === false ? { tiposVeiculo: [] } : {}) }
            : {}),
          razaoSocial: r.razaoSocial || p.razaoSocial,
          nomeFantasia: r.nomeFantasia || p.nomeFantasia,
          rua: r.rua || p.rua, numero: r.numero || p.numero, bairro: r.bairro || p.bairro,
          cidade: r.cidade || p.cidade, estado: r.uf || p.estado, cep: r.cep || p.cep,
          telefone: r.telefone || p.telefone, celular: r.celular || p.celular,
          email: r.email || p.email,
          atividade: r.atividadePrincipal || p.atividade,
          simplesNacional: r.simplesNacional || p.simplesNacional,
          inscricaoEstadual: p.inscricaoEstadual || r.inscricaoEstadual,
          responsavelNome: p.responsavelNome || r.representanteNome,
          responsavelCargo: p.responsavelCargo || r.representanteCargo,
          // O representante legal e a função dele vêm do próprio QSA do CNPJ —
          // um documento a menos para a equipe caçar depois.
          representanteNome: p.representanteNome || r.representanteNome,
          representanteCargo: p.representanteCargo || r.representanteCargo,
        }));
        // Posto: consulta a ANP (via proxy) e preenche autorização, bandeira e combustíveis
        const tipoFinal = detectado || data.tipoEstabelecimento;
        setAnpInfo(null);
        setAnacInfo(null);
        if (tipoFinal === "posto") {
          const anp = await fetchANP(digits);
          if (anp) { setData((p) => ({ ...p, perfil: { ...(p.perfil || {}), ...anpParaPerfil(anp) } })); setAnpInfo(anp); }
        }
        // Oficina de aeronave: COM, situação e categorias pelos dados abertos da ANAC
        if (tipoFinal === "oficina_aeronautica") {
          const anac = await fetchANAC(digits);
          if (anac) {
            const patch = anacParaPerfil(anac);
            setData((p) => {
              const atual = p.perfil || {};
              const junta = (k) => (patch[k] ? { [k]: Array.from(new Set([...(atual[k] || []), ...patch[k]])) } : {});
              return { ...p, perfil: { ...atual, ...patch, ...junta("categorias_classes"), ...junta("capacidades"), ...junta("convalidacoes") } };
            });
            setAnacInfo(anac);
          }
        }
      }
    }
  };

  const handleCEP = async (val) => {
    const masked = maskCEP(val);
    set({ cep: masked });
    const digits = masked.replace(/\D/g, "");
    if (digits.length === 8) {
      setCepLoading(true);
      const r = await fetchCEP(digits);
      setCepLoading(false);
      if (r) setData((p) => ({ ...p, rua: r.rua || p.rua, bairro: r.bairro || p.bairro, cidade: r.cidade || p.cidade, estado: r.uf || p.estado }));
    }
  };

  // Captura: envia a foto DIRETO para o Storage (sem decodificar em memória)
  const handlePickDoc = async (tipo, files) => {
    if (!files.length) return;
    setProcessingDocs(true);
    for (const original of files) {
      // Foto reduzida (HEIC vira JPEG) e extensão limpa — ver prepararUpload.
      const { file: f, ext, contentType } = await prepararUpload(original);
      const path = `documentos/${idRef.current}-${generateId().slice(0, 8)}.${ext}`;
      try {
        await uploadToStorage(path, f, contentType);
        setPendingDocs((prev) => [...prev, { tipo, path, nome: f.name || `documento.${ext}` }]);
      } catch (err) {
        setErrors((p) => ({ ...p, submit: "Falha ao enviar o arquivo: " + (err.message || "tente novamente") }));
      }
    }
    setProcessingDocs(false);
  };
  // Remove um anexo, seja da ficha salva (edição) ou recém-enviado
  const removeAnyDoc = (path) => {
    if (existingDocs.some((d) => d.path === path)) removeExistingDoc(path);
    else removePending(path);
  };

  const openDoc = async (path) => {
    const win = window.open("", "_blank");
    const url = await getSignedUrl(path);
    if (!url) { if (win) win.close(); return; }
    if (win) win.location.href = url; else window.location.href = url;
  };

  const removePending = async (path) => {
    setPendingDocs((prev) => prev.filter((d) => d.path !== path));
    try { await removeFromStorage([path]); } catch { /* ignora */ }
  };
  const removeExistingDoc = (path) => {
    setExistingDocs((prev) => prev.filter((d) => d.path !== path));
    setRemovedDocs((prev) => [...prev, path]);
  };

  // Estados com taxa definida no cadastro (regioes_comerciais): sem condição,
  // as perguntas comerciais dão lugar ao aviso, e a ficha não conclui.
  const [ufsExclusivas, setUfsExclusivas] = useState([]);
  useEffect(() => { if (open) ufsCondicaoExclusiva().then(setUfsExclusivas); }, [open]); // eslint-disable-line
  const aDefinir = data.tipoEstabelecimento === "posto" && condicoesADefinir(data.estado, data.perfil, ufsExclusivas);
  useEffect(() => {
    if (data.tipoEstabelecimento !== "posto") return;
    if (!!data.perfil?.condicoesADefinir === aDefinir) return;
    setData((d) => {
      const perfil = { ...(d.perfil || {}) };
      if (aDefinir) perfil.condicoesADefinir = true; else delete perfil.condicoesADefinir;
      return { ...d, perfil };
    });
  }, [aDefinir, data.tipoEstabelecimento]); // eslint-disable-line

  const validate = (concluir) => {
    const err = {};
    if (!data.razaoSocial.trim() && !data.nomeFantasia.trim())
      err.razaoSocial = "Informe ao menos a razão social ou nome fantasia";
    // Obrigatórios do ramo (na aeronáutica, os requisitos ANAC) — só ao concluir
    const faltaRamo = concluir ? faltandoNoPerfil(data.tipoEstabelecimento, data.perfil) : [];
    if (faltaRamo.length) err.perfil = `Para concluir, preencha: ${faltaRamo.join(", ")}`;
    if (concluir && aDefinir) err.perfil = `Posto em ${String(data.estado).toUpperCase()}: crie a condição comercial exclusiva antes de concluir (a taxa é definida no cadastro).`;
    // Socorro/assistência: só cobrado ao concluir (o rascunho pode ficar em
    // aberto), e só quando o modelo pergunta (oficina terrestre ou rastreamento)
    if (concluir && temSocorro && data.fazSocorro === null)
      err.socorro = "Responda se o parceiro presta socorro 24h ou assistência em rodovias";
    else if (concluir && temSocorro && data.fazSocorro) {
      if (!data.tiposAssistencia.length) err.socorro = "Marque os tipos de assistência prestada";
      else if (!data.atende24h && (!data.diasAtendimento.length || !data.horarioInicio || !data.horarioFim))
        err.socorro = "Informe os dias e o horário do socorro (vai para a rede de assistência do rastreamento)";
    }
    // Para CONCLUIR o fornecedor precisa ter aceitado os termos (substitui a assinatura)
    if (concluir && !aceitou)
      err.aceite = "Para concluir, leia e marque o aceite dos termos do fornecedor.";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const buildTextos = () => {
    const tv = data.tiposVeiculo.map((id) => TIPOS_VEICULO.find((t) => t.id === id)?.label).filter(Boolean);
    const esp = data.especialidades.map((id) => MECH_SPECIALTIES.find((s) => s.id === id)?.label).filter(Boolean);
    const serv = data.servicos.map(servicoLabel).filter(Boolean);
    // O que é do ramo (bandeira, combustíveis, produtos) entra antes: é o que
    // identifica o parceiro na leitura do termo.
    const ramo = resumoPerfil(data.tipoEstabelecimento, data.perfil, { comercial: false }).map((l) => `${l.k}: ${l.v}`);
    const especialidadesTexto = [ramo.join(". "), serv.join(", "), esp.join(", "), tv.length ? `Atende: ${tv.join(", ")}` : ""].filter(Boolean).join(". ");
    const assistenciaTexto = data.fazSocorro
      ? [
          (data.tiposAssistencia.map((id) => ASSISTENCIA_TIPOS.find((a) => a.id === id)?.label).filter(Boolean)).join(", "),
          resumoAtendimento(data),
          data.assistenciaRodovias ? `Atende rodovias${data.rodoviasAtendidas ? `: ${data.rodoviasAtendidas}` : ""}` : "",
          data.telefoneSocorro ? `Plantão: ${data.telefoneSocorro}` : "",
        ].filter(Boolean).join(". ")
      : "Não presta socorro 24h nem assistência em rodovias.";
    const enderecoCompleto = [[data.rua, data.numero].filter(Boolean).join(", "), data.bairro, data.cep].filter(Boolean).join(" - ");
    const municipioUf = [data.cidade, data.estado].filter(Boolean).join(" / ");
    const condicoesTexto = condicoesComerciaisTexto(data.tipoEstabelecimento, data.perfil);
    return { especialidadesTexto, condicoesTexto, assistenciaTexto, enderecoCompleto, municipioUf };
  };

  const clearDraft = () => {
    if (editing) return;
    try { localStorage.removeItem(DRAFT_KEY); localStorage.removeItem(DRAFT_KEY + "_docs"); localStorage.removeItem(DRAFT_KEY + "_sig"); } catch { /* ignora */ }
  };
  const resetState = () => {
    setData({ ...empty }); setErrors({}); setPendingDocs([]); setExistingDocs([]);
    setRemovedDocs([]); setExistingSignatureUrl(null); setSaveMsg("");
  };

  const handleSubmit = async (concluir) => {
    if (!validate(concluir)) return;

    // AQUI HAVIA UMA CONSULTA QUE ABORTAVA O "CONCLUIR".
    //
    // Ela perguntava aos sistemas o que faltava de documento e, se faltasse
    // qualquer coisa, recusava concluir: a ficha voltava para rascunho por causa
    // de um cartão CNPJ, e alguém repetia o mesmo clique dias depois. Não faz
    // mais sentido — o aceite fecha com o documento de quem assinou, e os
    // documentos da empresa são cobrados no detalhe da ficha ("Pedir documentos
    // no WhatsApp"), que é onde a equipe já vai atrás deles.

    setSaving(true);
    setSaveMsg(isEdit ? "Regerando ficha..." : "Gerando ficha...");
    try {
      const id = idRef.current;
      const today = getToday();
      const { especialidadesTexto, condicoesTexto, assistenciaTexto, enderecoCompleto, municipioUf } = buildTextos();

      // Assinatura desenhada foi substituída pelo aceite dos termos. Fichas antigas
      // que já tinham rubrica mantêm a imagem no PDF (compatibilidade).
      let assinaturaDataUrl = existingSignatureUrl || null;
      if (!assinaturaDataUrl && editing?.assinaturaPath) {
        try {
          const u = await getSignedUrl(editing.assinaturaPath);
          if (u) {
            const b = await (await fetch(u)).blob();
            assinaturaDataUrl = await new Promise((res) => { const rd = new FileReader(); rd.onloadend = () => res(rd.result); rd.readAsDataURL(b); });
          }
        } catch { /* mantém null */ }
      }

      // 1. PDF preenchido/assinado
      const tipoEstabLabel = TIPOS_ESTABELECIMENTO.find((t) => t.id === data.tipoEstabelecimento)?.label || "";
      // Comprovante de aceite: o que foi lido e marcado no click-wrap agora.
      const comprovante = aceiteInfo.pronto
        ? aceiteInfo.aceites.map((a) => ({
            modulo: a.modulo, codigo: a.codigo, versao: a.versao, hash: a.hash_sha256,
            nome: data.responsavelNome, cpf: data.responsavelDoc,
            aceito_em: new Date().toISOString(), origem: "crm", dispositivo: "desktop",
          }))
        : [];
      const blob = await gerarTermoPDF({
        tipoEstabelecimento: tipoEstabLabel,
        razaoSocial: data.razaoSocial, nomeFantasia: data.nomeFantasia, cnpj: data.cnpj,
        inscricaoEstadual: data.inscricaoEstadual, atividade: data.atividade, simplesNacional: data.simplesNacional,
        rua: data.rua, numero: data.numero, bairro: data.bairro, cep: data.cep,
        cidade: data.cidade, estado: data.estado,
        enderecoCompleto, municipioUf, telefone: data.telefone, celular: data.celular, email: data.email,
        especialidadesTexto, condicoesTexto, assistenciaTexto,
        responsavelNome: data.responsavelNome, responsavelDoc: data.responsavelDoc,
        responsavelRg: data.responsavelRg, responsavelTelefone: data.responsavelTelefone,
        responsavelCargo: data.responsavelCargo, responsavelEmail: data.responsavelEmail,
        banco: data.banco, agencia: data.agencia, contaCorrente: data.contaCorrente,
        tipoConta: data.tipoConta, titularConta: data.titularConta, chavePix: data.chavePix,
        situacao: "ATIVO", dataAssinatura: data.dataAssinatura,
        // No termo, o representante é quem assinou (é a assinatura que o PDF
        // carrega). O representante legal da empresa fica no cadastro.
        representanteNome: data.responsavelNome, representanteDoc: data.responsavelDoc,
        assinaturaDataUrl, aceiteTermos: aceitou, comprovante,
      });

      // 2. Uploads (ficha + assinatura). As fotos já foram enviadas na captura.
      setSaveMsg("Salvando arquivos...");
      const fichaPath = editing?.fichaPath || `geradas/${id}.pdf`;
      await uploadToStorage(fichaPath, blob, "application/pdf");

      const assinaturaPath = editing?.assinaturaPath || null;

      const documentosPaths = [...existingDocs, ...pendingDocs];
      if (removedDocs.length) { try { await removeFromStorage(removedDocs); } catch { /* ignora */ } }

      // Histórico de alterações (auditoria)
      const hist = Array.isArray(editing?.historico) ? [...editing.historico] : [];
      const stamp = (acao, detalhe) => hist.push({ ts: new Date().toISOString(), acao, detalhe: detalhe || "", autor: userName || "" });
      if (!editing) stamp("criacao", "Ficha criada");
      else stamp("edicao", "Dados atualizados");
      if (aceitou && !editing?.aceiteTermos) stamp("aceite", "Termos aceitos pelo fornecedor");
      if (pendingDocs.length) stamp("anexo", `${pendingDocs.length} documento(s) anexado(s)`);
      if (removedDocs.length) stamp("remocao", `${removedDocs.length} documento(s) removido(s)`);
      if (concluir && editing?.status !== "concluido") stamp("conclusao", "Credenciamento concluído");

      const novoStatus = concluir ? "concluido" : (editing?.status === "concluido" ? "concluido" : "pendente");

      // 3. Registro
      setSaveMsg("Registrando...");
      const record = {
        ...(editing || {}),
        id, userId: editing?.userId || userId, partnerId: data.partnerId || null,
        sistemas: data.sistemas, tipoEstabelecimento: data.tipoEstabelecimento,
        razaoSocial: data.razaoSocial, nomeFantasia: data.nomeFantasia, cnpj: data.cnpj,
        inscricaoEstadual: data.inscricaoEstadual, atividade: data.atividade, simplesNacional: data.simplesNacional,
        rua: data.rua, numero: data.numero, bairro: data.bairro, cidade: data.cidade,
        estado: data.estado, cep: data.cep, pais: data.pais,
        enderecoCompleto, telefone: data.telefone, celular: data.celular, email: data.email,
        responsavelNome: data.responsavelNome, responsavelDoc: data.responsavelDoc,
        responsavelRg: data.responsavelRg, responsavelTelefone: data.responsavelTelefone, responsavelCargo: data.responsavelCargo, responsavelEmail: data.responsavelEmail,
        representanteNome: data.representanteNome || null, representanteCargo: data.representanteCargo || null,
        banco: data.banco, agencia: data.agencia, contaCorrente: data.contaCorrente,
        tipoConta: data.tipoConta, titularConta: data.titularConta, chavePix: data.chavePix,
        servicos: perfilAtual?.catalogo === false ? [] : data.servicos,
        especialidades: perfilAtual?.catalogo === false ? [] : data.especialidades,
        tiposVeiculo: perfilAtual?.veiculos === false ? [] : data.tiposVeiculo,
        perfil: perfilAtual ? limparPerfil(data.tipoEstabelecimento, data.perfil) : {},
        fazSocorro: !!data.fazSocorro,
        atende24h: !!data.fazSocorro && !!data.atende24h,
        assistenciaRodovias: !!data.fazSocorro && !!data.assistenciaRodovias,
        tiposAssistencia: data.fazSocorro ? data.tiposAssistencia : [],
        diasAtendimento: data.fazSocorro && !data.atende24h ? data.diasAtendimento : [],
        horarioInicio: data.fazSocorro && !data.atende24h ? data.horarioInicio || null : null,
        horarioFim: data.fazSocorro && !data.atende24h ? data.horarioFim || null : null,
        telefoneSocorro: data.fazSocorro ? data.telefoneSocorro || null : null,
        rodoviasAtendidas: data.fazSocorro && data.assistenciaRodovias ? data.rodoviasAtendidas || null : null,
        situacao: "ATIVO", dataAssinatura: data.dataAssinatura, aceiteTermos: aceitou,
        assinaturaPath, fichaPath,
        documentoPath: documentosPaths[0]?.path || null,
        documentos: documentosPaths,
        status: novoStatus, historico: hist, notes: data.notes,
        createdAt: editing?.createdAt || today, updatedAt: today,
      };
      // 4. Grava no banco de forma CONFIÁVEL — e só limpa o rascunho / fecha
      //    DEPOIS de confirmar. Antes, rascunho só chamava setCredenciamentos
      //    (sync assíncrono do hook, sem aviso) e o concluir engolia o erro do
      //    upsert; uma instabilidade do Supabase perdia a ficha em silêncio.
      //    upsert por id → um reenvio é idempotente (nunca duplica).
      setSaveMsg("Salvando no banco...");
      try {
        await upsertRow("credenciamentos", record, record.userId);
      } catch (e) {
        console.error("upsert credenciamento falhou:", e);
        setSaveMsg("");
        setSaving(false);
        // O motivo real vai junto. A mensagem antiga dizia "conexão instável"
        // para QUALQUER falha — e por meses culpou a rede por um erro de nome
        // de coluna, que nenhuma tentativa a mais resolveria.
        setErrors((prev) => ({
          ...prev,
          submit: `Não foi possível salvar agora. Seus dados NÃO foram perdidos — continuam aqui na tela. Tente de novo em instantes; se repetir, mande esta linha para o TI: ${e.message || "erro desconhecido"}`,
        }));
        return; // mantém o rascunho e o modal aberto — nada é limpo
      }

      // Cartão CNPJ direto da Receita — um documento a menos para pedir. Roda
      // depois de gravar (a serverless lê a ficha do banco) e não gasta consulta
      // se a ficha já tiver o documento.
      try {
        const r = await emitirCartaoCNPJ(id);
        if (r?.ok && Array.isArray(r.documentos)) {
          // A serverless já gravou no banco; espelhar aqui evita a tela mostrar
          // a ficha sem o cartão até o próximo carregamento.
          record.documentos = r.documentos;
          record.documentoPath = record.documentoPath || r.path || null;
          if (r.inscricao_estadual && !record.inscricaoEstadual) record.inscricaoEstadual = r.inscricao_estadual;
        }
      } catch { /* sem cartão agora: o detalhe da ficha tem o botão de emitir */ }

      // Estado local (idempotente: substitui se já existir, nunca duplica)
      setCredenciamentos((prev) => (prev.some((c) => c.id === id) ? prev.map((c) => (c.id === id ? record : c)) : [...prev, record]));

      // 5. Concluiu → avisa os sistemas. A ficha JÁ está no banco; se algum
      //    sistema estiver fora, o erro fica para o botão de reenvio no detalhe.
      if (concluir) {
        try {
          if (aceitou && !editing?.aceiteTermos) {
            await registrarAceite(id, {
              aceitante: { nome: data.responsavelNome, cpf: data.responsavelDoc, email: data.responsavelEmail, cargo: data.responsavelCargo },
              origem: "crm",
            }).catch(() => null);
          }
          const alvos = data.sistemas?.length ? data.sistemas : ["manutencao"];
          Promise.all(alvos.map((sis) => sincronizarAceite(id, sis).catch(() => null)))
            .then((rs) => {
              const sincs = Object.fromEntries(rs.map((r, i) => [alvos[i], r?.sync]).filter(([, v]) => v));
              if (Object.keys(sincs).length) {
                setCredenciamentos((prev) => prev.map((c) => (c.id === id ? { ...c, integracaoSync: { ...(c.integracaoSync || {}), ...sincs } } : c)));
              }
            })
            .catch(() => { /* fica para o botão de reenvio no detalhe da ficha */ });
        } catch { /* idem: a ficha está salva, o aviso se resolve no reenvio */ }
      }

      // 6. Cópia local do PDF
      baixarBlob(blob, `Ficha_${(data.nomeFantasia || data.razaoSocial || "credenciamento").replace(/\s+/g, "_")}.pdf`);

      clearDraft();
      resetState();
      onClose();
    } catch (e) {
      console.error(e);
      setSaveMsg("");
      setErrors((prev) => ({ ...prev, submit: "Erro ao salvar: " + (e.message || "tente novamente") }));
    } finally {
      setSaving(false);
    }
  };

  // Cancelar/fechar sem salvar: remove as fotos órfãs já enviadas e limpa rascunho
  const handleCancel = async () => {
    if (pendingDocs.length) { try { await removeFromStorage(pendingDocs.map((d) => d.path)); } catch { /* ignora */ } }
    clearDraft();
    resetState();
    onClose();
  };

  // Link/QR para o parceiro concluir no próprio celular (foto do documento inclusive)
  //
  // Numa ficha que JÁ EXISTE, o link precisa reabrir ESTA ficha — é o token de
  // correção (`c` = id, `k` = edit_token). Ele nascia sempre como link de ficha
  // NOVA: o fornecedor preenchia tudo, o formulário dava INSERT, e o CRM ficava
  // com duas fichas da mesma empresa — a do registro rápido, vazia e em "a
  // contatar", e a preenchida, solta, sem vínculo nenhum com a primeira. Era
  // por isso que "não vinculava uma ficha na outra": não havia o que vincular,
  // eram dois cadastros separados desde o começo.
  //
  // Ficha concluída não entra na RPC de correção (ela recusa `status =
  // concluido`), e aí o link de ficha nova continua sendo o certo.
  const reabreEstaFicha = isEdit && !!editing?.editToken && editing?.status !== "concluido";
  const linkToken = reabreEstaFicha
    ? encodeFichaToken({
        c: editing.id, k: editing.editToken, s: data.sistemas, t: modelo.id,
        n: data.nomeFantasia || data.razaoSocial || null,
      })
    : encodeFichaToken({
        p: data.partnerId || null, u: userId || null, s: data.sistemas, t: modelo.id,
        n: data.nomeFantasia || data.razaoSocial || null,
      });

  // Catálogo da manutenção filtrado pelo ramo (o já marcado nunca some da tela)
  const sugeridos = perfilAtual?.servicosSugeridos;
  const temFiltroServicos = !!sugeridos?.length;
  const listaServicos = !temFiltroServicos || verTodosServicos
    ? SERVICOS_TERRESTRES.concat(SERVICOS_PRESTADOS.filter((s) => s.aeronautico && data.servicos.includes(s.id)))
    : SERVICOS_PRESTADOS.filter((s) => sugeridos.includes(s.id) || data.servicos.includes(s.id));

  const veiculosVisiveis = SPECIALTY_GROUPS.filter((g) =>
    ["geral", "comercio"].includes(g.veic) ||
    data.tiposVeiculo.length === 0 ||
    data.tiposVeiculo.includes(g.veic) ||
    g.items.some((it) => data.especialidades.includes(it.id))
  );

  return (
    <Modal open={open} onClose={saving ? () => {} : handleCancel} title={isEdit ? "Editar Ficha de Credenciamento" : "Nova Ficha de Credenciamento"} w={720} closeOnBackdrop={false}>
      {/* Sistemas */}
      <Field label="Aparece nos sistemas">
        <Chips options={SISTEMAS} selected={data.sistemas} onToggle={(id) => toggle("sistemas", id)} cols={3} />
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
        <Field label="Tipo de estabelecimento" help="O ramo escolhido muda as perguntas do bloco Serviços prestados.">
          <select style={selectStyle} value={data.tipoEstabelecimento} onChange={(e) => trocarTipo(e.target.value)}>
            {TIPOS_ESTABELECIMENTO.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Vincular a parceiro cadastrado (opcional)">
          <BuscaParceiro partners={partners} value={data.partnerId} onChange={handlePartner} />
        </Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
        <Field label="CNPJ">
          <div style={{ position: "relative" }}>
            <input style={inputStyle} value={data.cnpj} onChange={(e) => handleCNPJ(e.target.value)} placeholder="00.000.000/0000-00" />
            {cnpjLoading && <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "#64748b" }}>Buscando...</span>}
          </div>
        </Field>
        <Field label="Telefone fixo">
          <input style={inputStyle} value={data.telefone} onChange={(e) => set({ telefone: maskPhone(e.target.value) })} placeholder="(00) 0000-0000" />
        </Field>
        <Field label="Celular">
          <input style={inputStyle} value={data.celular} onChange={(e) => set({ celular: maskPhone(e.target.value) })} placeholder="(00) 00000-0000" />
        </Field>
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="E-mail (para criar o acesso ao sistema)">
            <input style={inputStyle} value={data.email} onChange={(e) => set({ email: e.target.value })} placeholder="email@exemplo.com" />
          </Field>
        </div>
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="Razão Social" required error={errors.razaoSocial}>
            <input style={errors.razaoSocial ? inputErrorStyle : inputStyle} value={data.razaoSocial} onChange={(e) => set({ razaoSocial: e.target.value })} placeholder="Razão social da oficina" />
          </Field>
        </div>
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="Nome Fantasia">
            <input style={inputStyle} value={data.nomeFantasia} onChange={(e) => set({ nomeFantasia: e.target.value })} placeholder="Nome fantasia" />
          </Field>
        </div>
        <Field label="Inscrição Estadual"><input style={inputStyle} value={data.inscricaoEstadual} onChange={(e) => set({ inscricaoEstadual: e.target.value })} placeholder="Isento, se não tiver" /></Field>
        <Field label="Ramo de Atividade"><input style={inputStyle} value={data.atividade} onChange={(e) => set({ atividade: e.target.value })} placeholder="Ex: Manutenção de veículos" /></Field>
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="Optante pelo Simples Nacional">
            <select style={selectStyle} value={data.simplesNacional} onChange={(e) => set({ simplesNacional: e.target.value })}>
              <option value="">— Não informado —</option>
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </select>
          </Field>
        </div>
      </div>

      {/* Endereço */}
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "6px 0 8px" }}>Endereço</div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "0 12px" }}>
        <Field label="Rua / Logradouro"><input style={inputStyle} value={data.rua} onChange={(e) => set({ rua: e.target.value })} /></Field>
        <Field label="Número"><input style={inputStyle} value={data.numero} onChange={(e) => set({ numero: e.target.value })} /></Field>
        <Field label="Bairro"><input style={inputStyle} value={data.bairro} onChange={(e) => set({ bairro: e.target.value })} /></Field>
        <Field label="CEP">
          <div style={{ position: "relative" }}>
            <input style={inputStyle} value={data.cep} onChange={(e) => handleCEP(e.target.value)} placeholder="00000-000" inputMode="numeric" />
            {cepLoading && <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "#64748b" }}>Buscando...</span>}
          </div>
        </Field>
        <Field label="Cidade"><input style={inputStyle} value={data.cidade} onChange={(e) => set({ cidade: e.target.value })} /></Field>
        <Field label="Estado (UF)">
          <select style={selectStyle} value={data.estado} onChange={(e) => set({ estado: e.target.value })}>
            {BRAZILIAN_STATES.map((s) => <option key={s.uf} value={s.uf}>{s.uf}</option>)}
          </select>
        </Field>
        <Field label="País"><input style={inputStyle} value={data.pais} onChange={(e) => set({ pais: e.target.value })} /></Field>
      </div>

      {/* Quem assina a ficha (pode não ser o representante legal) */}
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "6px 0 8px" }}>Quem assina a ficha</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
        <div style={{ gridColumn: "1/-1" }}><Field label="Nome do responsável"><input style={inputStyle} value={data.responsavelNome} onChange={(e) => set({ responsavelNome: e.target.value })} /></Field></div>
        <Field label="CPF"><input style={inputStyle} value={data.responsavelDoc} onChange={(e) => set({ responsavelDoc: e.target.value })} placeholder="000.000.000-00" /></Field>
        <Field label="RG"><input style={inputStyle} value={data.responsavelRg} onChange={(e) => set({ responsavelRg: e.target.value })} /></Field>
        <Field label="Telefone"><input style={inputStyle} value={data.responsavelTelefone} onChange={(e) => set({ responsavelTelefone: maskPhone(e.target.value) })} placeholder="(00) 00000-0000" /></Field>
        <Field label="Cargo"><input style={inputStyle} value={data.responsavelCargo} onChange={(e) => set({ responsavelCargo: e.target.value })} placeholder="Ex: Sócio, Gerente" /></Field>
        <div style={{ gridColumn: "1/-1" }}><Field label="E-mail"><input style={inputStyle} value={data.responsavelEmail} onChange={(e) => set({ responsavelEmail: e.target.value })} placeholder="email@exemplo.com" /></Field></div>
      </div>

      {/* Representante legal da empresa — 2ª etapa. Já vem preenchido do QSA do
          CNPJ quando a Receita devolve o sócio administrador. */}
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "6px 0 8px" }}>Representante legal da empresa</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
        <Field label="Nome" help="Quem responde pela empresa (se for diferente de quem assinou)."><input style={inputStyle} value={data.representanteNome} onChange={(e) => set({ representanteNome: e.target.value })} /></Field>
        <Field label="Função na empresa"><input style={inputStyle} value={data.representanteCargo} onChange={(e) => set({ representanteCargo: e.target.value })} placeholder="Ex: Sócio-administrador, Diretor" /></Field>
      </div>

      {/* Dados bancários */}
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "6px 0 8px" }}>Dados bancários</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
        <Field label="Banco"><input style={inputStyle} value={data.banco} onChange={(e) => set({ banco: e.target.value })} placeholder="Ex: 341 - Itaú" /></Field>
        <Field label="Agência"><input style={inputStyle} value={data.agencia} onChange={(e) => set({ agencia: e.target.value })} /></Field>
        <Field label="Conta"><input style={inputStyle} value={data.contaCorrente} onChange={(e) => set({ contaCorrente: e.target.value })} /></Field>
        <Field label="Tipo de conta">
          <select style={selectStyle} value={data.tipoConta} onChange={(e) => set({ tipoConta: e.target.value })}>
            <option value="">— Não informado —</option>
            <option value="corrente">Corrente</option>
            <option value="poupanca">Poupança</option>
          </select>
        </Field>
        <Field label="Titular da conta (se diferente da empresa)"><input style={inputStyle} value={data.titularConta} onChange={(e) => set({ titularConta: e.target.value })} /></Field>
        <Field label="Chave PIX"><input style={inputStyle} value={data.chavePix} onChange={(e) => set({ chavePix: e.target.value })} /></Field>
      </div>

      {/* Serviços prestados — MESMA lista do sistema de manutenção */}
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "6px 0 8px" }}>{tituloSecaoServicos(secoes, data.tipoEstabelecimento).titulo}</div>
      {data.tipoEstabelecimento === "oficina_aeronautica" && anacInfo && (() => {
        const ruim = SITUACOES_IMPEDITIVAS.includes(anacInfo.situacao);
        return (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: ruim ? "#fef2f2" : "#f0fdf4", border: `1px solid ${ruim ? "#fecaca" : "#bbf7d0"}`, borderRadius: 8, padding: "9px 12px", marginBottom: 12 }}>
            <CheckCircle2 size={15} color={ruim ? "#dc2626" : "#16a34a"} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 12, color: ruim ? "#991b1b" : "#166534", lineHeight: 1.4 }}>
              <strong>{ruim ? `COM ${anacInfo.situacao.toLowerCase()} na ANAC.` : "Oficina encontrada na ANAC."}</strong> COM {anacInfo.numeroCom} — {anacInfo.razaoSocial}.
              {ruim ? " Não pode liberar aeronave como organização certificada." : " Situação, COM e categorias preenchidos. Rádio, Instrumento e Acessório não constam no arquivo público — confirme pela EO."}
            </div>
          </div>
        );
      })()}
      {errors.perfil && <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 10 }}>{errors.perfil}</div>}
      {data.tipoEstabelecimento === "posto" && anpInfo && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "9px 12px", marginBottom: 12 }}>
          <CheckCircle2 size={15} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12, color: "#166534", lineHeight: 1.4 }}>
            <strong>Posto encontrado na ANP.</strong> {anpInfo.razaoSocial}{anpInfo.municipio ? ` — ${anpInfo.municipio}/${anpInfo.uf}` : ""}{anpInfo.autorizacao ? ` · Autorização ${anpInfo.autorizacao}` : ""}. Bandeira e combustíveis preenchidos automaticamente.
          </div>
        </div>
      )}
      {/* Tabela comercial do posto. Depois do aceite ela não muda (o banco
          recusa — R-127); para reduzir, a taxa negociada abaixo. */}
      {data.tipoEstabelecimento === "posto" && (
        <EscolherTabela
          value={data.perfil?.tabelaComercial?.codigo || ""}
          atual={data.perfil?.tabelaComercial || null}
          estado={data.estado}
          nomePosto={data.nomeFantasia || data.razaoSocial}
          disabled={!!editing?.aceiteTermos}
          aviso={editing?.aceiteTermos ? "O posto já aceitou estas condições — a tabela não muda (R-127). Para reduzir a taxa, use a taxa negociada." : ""}
          onEscolher={(linha) => {
            const perfil = { ...(data.perfil || {}) };
            if (linha) perfil.tabelaComercial = copiaDaTabela(linha); else delete perfil.tabelaComercial;
            // Prazo ou forma que não existem na tabela nova não ficam escolhidos.
            const prazos = (perfil.tabelaComercial?.prazos || null);
            if (prazos && !prazos.some((p) => p.id === perfil.prazoRepasse)) delete perfil.prazoRepasse;
            if (!linha && !["semanal", "quinzenal", "mensal"].includes(perfil.prazoRepasse)) delete perfil.prazoRepasse;
            delete perfil.formaPagamento;
            set({ perfil });
          }}
        />
      )}
      <PerfilCampos tipo={data.tipoEstabelecimento} perfil={data.perfil} onChange={(perfil) => set({ perfil })} variant="crm" />
      {secoes.veiculos && perfilAtual?.veiculos !== false && (
        <Field label="Tipos de veículo atendidos">
          <Chips options={TIPOS_VEICULO} selected={data.tiposVeiculo} onToggle={(id) => toggle("tiposVeiculo", id)} cols={3} />
        </Field>
      )}
      {secoes.servicos && (
      <>
      <Field label="Serviços prestados" help="Mesma lista do sistema de manutenção — o que for marcado aqui casa com o cadastro de lá, sem retrabalho.">
        <Chips options={listaServicos} selected={data.servicos} onToggle={(id) => toggle("servicos", id)} cols={2} />
        {temFiltroServicos && (
          <button
            type="button"
            onClick={() => setVerTodosServicos((v) => !v)}
            style={{ marginTop: 6, padding: "5px 10px", borderRadius: 7, border: "1px solid #ddd6fe", background: "#fff", color: "#6d28d9", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
          >
            {verTodosServicos ? "Mostrar só os do ramo" : `Ver todos os serviços (${SERVICOS_TERRESTRES.length})`}
          </button>
        )}
        {data.servicos.length > 0 && (
          <div style={{ fontSize: 10, color: "#64748b", marginTop: 6 }}>{data.servicos.length} serviço(s) selecionado(s).</div>
        )}
      </Field>

      {/* Especialidades detalhadas (opcional, complementa os serviços acima) */}
      <div style={{ marginBottom: 13 }}>
        <button
          type="button"
          onClick={() => setShowEsp((v) => !v)}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#475569" }}
        >
          <span>Especialidades detalhadas (opcional){data.especialidades.length ? ` — ${data.especialidades.length} marcada(s)` : ""}</span>
          {showEsp ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
        {showEsp && (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 10, color: "#94a3b8" }}>Marque os tipos de veículo acima para ver as especialidades de cada um. "Geral" e "Comércio" aparecem sempre.</div>
            {veiculosVisiveis.map((g) => (
              <div key={g.veic}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{g.cat}</div>
                <Chips options={g.items} selected={data.especialidades} onToggle={(id) => toggle("especialidades", id)} cols={3} />
              </div>
            ))}
          </div>
        )}
      </div>
      </>
      )}

      {/* Socorro 24h / assistência em rodovias — alimenta a rede do rastreamento.
          Só aparece para os sistemas que usam essa rede (manutenção e
          rastreamento): num link só de combustível é pergunta de outro ramo. */}
      {secoes.socorro && (
      <>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "6px 0 8px" }}>Socorro 24h / Assistência em rodovias</div>
      <Field label="Presta socorro 24h ou assistência em rodovias?" error={errors.socorro}>
        <div style={{ display: "flex", gap: 8 }}>
          <Escolha on={data.fazSocorro === true} onClick={() => set({ fazSocorro: true })}>Sim, presta</Escolha>
          <Escolha on={data.fazSocorro === false} onClick={() => setData((p) => ({ ...p, fazSocorro: false, atende24h: false, assistenciaRodovias: false, tiposAssistencia: [], diasAtendimento: [], horarioInicio: "", horarioFim: "", telefoneSocorro: "", rodoviasAtendidas: "" }))}>Não presta</Escolha>
        </div>
      </Field>

      {data.fazSocorro === true && (
        <>
          <Field label="Tipos de assistência prestada">
            <Chips options={ASSISTENCIA_TIPOS} selected={data.tiposAssistencia} onToggle={(id) => toggle("tiposAssistencia", id)} cols={2} />
          </Field>
          <Field label="Atendimento">
            <div style={{ display: "flex", gap: 8 }}>
              <Escolha on={data.atende24h} onClick={() => set({ atende24h: true })}>24 horas, todos os dias</Escolha>
              <Escolha on={!data.atende24h} onClick={() => set({ atende24h: false })}>Em horário específico</Escolha>
            </div>
          </Field>
          {!data.atende24h && (
            <div style={{ padding: "12px 14px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", marginBottom: 13 }}>
              <Field label="Dias em que presta o socorro">
                <Chips options={DIAS_SEMANA} selected={data.diasAtendimento} onToggle={(id) => toggle("diasAtendimento", id)} cols={7} />
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <button type="button" onClick={() => set({ diasAtendimento: DIAS_UTEIS })} style={miniBtn}>Seg a Sex</button>
                  <button type="button" onClick={() => set({ diasAtendimento: DIAS_SEMANA.map((d) => d.id) })} style={miniBtn}>Todos os dias</button>
                </div>
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                <Field label="Das"><input type="time" style={inputStyle} value={data.horarioInicio} onChange={(e) => set({ horarioInicio: e.target.value })} /></Field>
                <Field label="Até"><input type="time" style={inputStyle} value={data.horarioFim} onChange={(e) => set({ horarioFim: e.target.value })} /></Field>
              </div>
              <div style={{ fontSize: 10, color: "#92400e" }}>Esse é o horário que a central de rastreamento vê ao acionar o parceiro.</div>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
            <Field label="Telefone de plantão (se diferente do comercial)">
              <input style={inputStyle} value={data.telefoneSocorro} onChange={(e) => set({ telefoneSocorro: maskPhone(e.target.value) })} placeholder="(00) 00000-0000" />
            </Field>
            <Field label="Atende ocorrência em rodovia?">
              <select style={selectStyle} value={data.assistenciaRodovias ? "sim" : "nao"} onChange={(e) => set({ assistenciaRodovias: e.target.value === "sim" })}>
                <option value="nao">Não</option>
                <option value="sim">Sim, atendemos em rodovia</option>
              </select>
            </Field>
          </div>
          {data.assistenciaRodovias && (
            <Field label="Rodovias / trechos atendidos">
              <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={data.rodoviasAtendidas} onChange={(e) => set({ rodoviasAtendidas: e.target.value })} placeholder="Ex: BR-116 km 200 ao 280, SP-070, anel viário" />
            </Field>
          )}
        </>
      )}
      </>
      )}

      {/* Documento de quem assina — o único que a ficha precisa agora */}
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "6px 0 8px" }}>Documento de quem assina (RG/CNH)</div>

      {/* Caminho recomendado: o parceiro fotografa pelo celular dele */}
      <div style={{ borderRadius: 10, border: "1px solid #ddd6fe", background: "#faf8ff", padding: 14, marginBottom: 13 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 700, color: "#5b21b6" }}>
          <Smartphone size={15} /> Recomendado: o parceiro envia pelo celular dele
        </div>
        <div style={{ fontSize: 11, color: "#6b21a8", lineHeight: 1.5, marginTop: 6 }}>
          Mostre o QR ou mande o link no WhatsApp. Ele fotografa o documento com a câmera do próprio celular e envia —
          sem depender da câmera do tablet, que trava com fotos grandes e derruba o preenchimento.
        </div>
        {/* Qual ficha este link abre. Sem isto ninguém tinha como saber que o
            link criava um cadastro separado — a duplicidade só aparecia depois,
            na lista. */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 8, padding: "7px 10px", borderRadius: 7, background: reabreEstaFicha ? "#ecfdf5" : "#fffbeb", border: `1px solid ${reabreEstaFicha ? "#a7f3d0" : "#fde68a"}`, fontSize: 10.5, lineHeight: 1.45, color: reabreEstaFicha ? "#065f46" : "#92400e" }}>
          {reabreEstaFicha
            ? <span>O que ele preencher cai <strong>nesta mesma ficha</strong> — não abre um cadastro novo.</span>
            : <span>Este link cria uma <strong>ficha nova</strong>. Para completar a ficha que já existe, salve-a primeiro e reabra por <strong>Continuar</strong>.</span>}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button type="button" onClick={() => setShowQR((v) => !v)} style={{ ...btnGhost, borderColor: "#c4b5fd", color: "#6d28d9" }}>
            {showQR ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {showQR ? "Ocultar QR" : "Mostrar QR"}
          </button>
          <a href={whatsappUrl(data.celular || data.telefone, linkToken, data.nomeFantasia || data.razaoSocial)} target="_blank" rel="noreferrer" style={{ ...btnGhost, textDecoration: "none", borderColor: "#a7f3d0", color: "#059669" }}>
            Enviar link no WhatsApp
          </a>
          <button type="button" onClick={() => { navigator.clipboard?.writeText(fichaFormUrl(linkToken)); }} style={btnGhost}>
            Copiar link
          </button>
        </div>
        {showQR && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
            <QRCode value={fichaFormUrl(linkToken)} size={200} style={{ border: "1px solid #e9d5ff" }} />
          </div>
        )}
      </div>

      <Field label="Ou anexe aqui" help="Etiquetado por tipo — assim conta como documento obrigatório na manutenção/combustível e o fornecedor não reenvia.">
        <DocsCredenciamento
          tipos={docsDaFicha("assinatura", todosDocs)}
          docs={todosDocs}
          onPick={handlePickDoc}
          onRemove={removeAnyDoc}
          onView={openDoc}
          isMobile={false}
          processing={processingDocs}
        />
        <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 6 }}>Cada arquivo é enviado na hora e fica anexado mesmo se o navegador fechar.</div>
      </Field>

      {/* 2ª etapa — não trava a conclusão. Fica recolhida para o cadastro em
          visita seguir reto; quem já está com os papéis na mão abre e anexa. */}
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "6px 0 8px" }}>Documentos da empresa (pedimos depois)</div>
      <div style={{ borderRadius: 10, border: "1px solid #bfdbfe", background: "#eff6ff", padding: "10px 12px", marginBottom: 13 }}>
        <div style={{ fontSize: 11.5, color: "#1d4ed8", lineHeight: 1.5 }}>
          {docsFaltandoAqui.length
            ? <>Faltam <strong>{docsFaltandoAqui.map((d) => d.label).join(", ")}</strong>. Não seguram esta ficha — depois de concluir, o detalhe da ficha tem o botão que pede tudo isso por WhatsApp.</>
            : <>Documentos da empresa completos.</>}
        </div>
        <button type="button" onClick={() => setShowDocsEmpresa((v) => !v)} style={{ ...btnGhost, marginTop: 9, borderColor: "#bfdbfe", color: "#1d4ed8" }}>
          {showDocsEmpresa ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {showDocsEmpresa ? "Ocultar" : "Anexar agora (se estiver com eles)"}
        </button>
        {showDocsEmpresa && (
          <div style={{ marginTop: 10 }}>
            <DocsCredenciamento
              tipos={docsDaFicha("complementares", todosDocs, data)}
              docs={todosDocs}
              onPick={handlePickDoc}
              onRemove={removeAnyDoc}
              onView={openDoc}
              isMobile={false}
              processing={processingDocs}
              // Anexo solto aparece no bloco de cima, que está sempre visível.
              outros={false}
            />
          </div>
        )}
      </div>

      {/* Data */}
      <Field label="Data da assinatura">
        <input type="date" style={{ ...inputStyle, maxWidth: 200 }} value={data.dataAssinatura} onChange={(e) => set({ dataAssinatura: e.target.value })} />
      </Field>

      {/* Aceite dos termos (click-wrap, substitui a assinatura) */}
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "6px 0 8px" }}>Aceite dos termos de uso</div>
      <div style={{ marginBottom: 13 }}>
        {isEdit && editing?.aceiteTermos && !aceiteInfo.pronto && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "9px 12px", fontSize: 12, color: "#166534", marginBottom: 8 }}>
            <CheckCircle2 size={15} color="#16a34a" /> Esta ficha já tem aceite registrado. Reafirme abaixo só se quiser atualizar a versão do termo.
          </div>
        )}
        <TermosAceite sistemas={data.sistemas} onChange={setAceiteInfo} />
        {errors.aceite && <div style={{ fontSize: 10, color: "#ef4444", marginTop: 3, fontWeight: 500 }}>{errors.aceite}</div>}
      </div>

      {errors.submit && <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 8, fontWeight: 500 }}>{errors.submit}</div>}

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 6 }}>
        {saving && <span style={{ fontSize: 12, color: "#64748b", display: "flex", alignItems: "center", gap: 6 }}><Loader2 size={14} className="spin" /> {saveMsg}</span>}
        <button onClick={handleCancel} disabled={saving} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.5 : 1 }}>Cancelar</button>
        <button onClick={() => handleSubmit(false)} disabled={saving || processingDocs} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", color: "#475569", fontSize: 13, fontWeight: 600, cursor: (saving || processingDocs) ? "not-allowed" : "pointer", opacity: (saving || processingDocs) ? 0.7 : 1 }}>
          Salvar rascunho
        </button>
        <button onClick={() => handleSubmit(true)} disabled={saving || processingDocs} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "#059669", color: "#fff", fontSize: 13, fontWeight: 600, cursor: (saving || processingDocs) ? "not-allowed" : "pointer", opacity: (saving || processingDocs) ? 0.7 : 1 }}>
          Concluir credenciamento
        </button>
      </div>
      <style>{`.spin { animation: spin 1s linear infinite } @keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </Modal>
  );
}

const btnGhost = {
  display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
  borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff",
  color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer",
};

const miniBtn = {
  padding: "5px 10px", borderRadius: 7, border: "1px solid #fcd34d",
  background: "#fff", color: "#92400e", fontSize: 11, fontWeight: 600, cursor: "pointer",
};

// Botão de escolha exclusiva (Sim/Não, 24h/horário)
function Escolha({ on, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1, padding: "10px", borderRadius: 8, cursor: "pointer", fontSize: 12,
        fontWeight: on ? 700 : 500, fontFamily: "inherit",
        border: on ? "1.5px solid #7c3aed" : "1px solid #e2e8f0",
        background: on ? "#f5f3ff" : "#fff", color: on ? "#6d28d9" : "#475569",
      }}
    >
      {children}
    </button>
  );
}
